import { currentTimestamp } from '@ardoq/date-time';
import { createPausableDeadline } from './pausableDeadline';

export const LAYOUT_WORKER_START_TIMEOUT_MS = 30_000;
export const LAYOUT_WORKER_ABANDON_MS = 180_000;
export const MAX_LAYOUT_WORKER_ATTEMPTS = 2;

export type StartFailureCause =
  | { reason: 'error'; event: Event }
  | { reason: 'timeout' }
  | { reason: 'abandoned' }
  | { reason: 'construction'; error: unknown };

export type StartTiming = {
  waitedMs: number;
  wallClockMs: number;
  wasHidden: boolean;
};

export type LayoutWorkerStartFailure = StartFailureCause &
  StartTiming & {
    attempt: number;
    next: 'newWorker' | 'mainThread' | 'mainThreadWhileWaiting';
  };

export type LateLayoutWorkerStart = StartTiming & { attempt: number };

export type LayoutTarget =
  | { kind: 'worker'; worker: Worker }
  | { kind: 'mainThread' };

export type LayoutWorkerConnection = {
  getTarget: () => Promise<LayoutTarget>;
};

type ConnectArgs = {
  createWorker: () => Worker;
  onWorkerCreated: (worker: Worker) => void;
  onStartFailure: (failure: LayoutWorkerStartFailure) => void;
  onLateStart: (lateStart: LateLayoutWorkerStart) => void;
};

type StartListeners = {
  onInitialized: (timing: StartTiming) => void;
  onError: (event: Event, timing: StartTiming) => void;
  onTimeout: (timing: StartTiming) => void;
  onAbandoned: (timing: StartTiming) => void;
};

const MAIN_THREAD: LayoutTarget = { kind: 'mainThread' };

const isInitializedMessage = (data: unknown): boolean =>
  Boolean(data) && typeof data === 'object' && 'initialized' in data!;

const isDocumentHidden = (): boolean => document.visibilityState === 'hidden';

const watchWorkerStart = (
  worker: Worker,
  { onInitialized, onError, onTimeout, onAbandoned }: StartListeners
): void => {
  const startedAt = performance.now();
  const startedAtWallClock = currentTimestamp();
  const visibility = { wasHidden: isDocumentHidden() };

  const timing = (): StartTiming => ({
    waitedMs: Math.round(performance.now() - startedAt),
    wallClockMs: currentTimestamp() - startedAtWallClock,
    wasHidden: visibility.wasHidden,
  });
  const deadlines = [
    createPausableDeadline(LAYOUT_WORKER_START_TIMEOUT_MS, () =>
      onTimeout(timing())
    ),
    createPausableDeadline(LAYOUT_WORKER_ABANDON_MS, () => {
      stopWatching();
      onAbandoned(timing());
    }),
  ];
  const onVisibilityChange = () => {
    if (isDocumentHidden()) {
      visibility.wasHidden = true;
      deadlines.forEach(deadline => deadline.pause());
      return;
    }
    deadlines.forEach(deadline => deadline.resume());
  };
  const onMessage = (event: MessageEvent) => {
    if (isInitializedMessage(event.data)) {
      stopWatching();
      onInitialized(timing());
    }
  };
  const onWorkerError = (event: Event) => {
    stopWatching();
    onError(event, timing());
  };
  const stopWatching = () => {
    deadlines.forEach(deadline => deadline.cancel());
    worker.removeEventListener('message', onMessage);
    worker.removeEventListener('error', onWorkerError);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };

  worker.addEventListener('message', onMessage);
  worker.addEventListener('error', onWorkerError);
  document.addEventListener('visibilitychange', onVisibilityChange);
  if (!isDocumentHidden()) {
    deadlines.forEach(deadline => deadline.resume());
  }
};

export const connectLayoutWorker = ({
  createWorker,
  onWorkerCreated,
  onStartFailure,
  onLateStart,
}: ConnectArgs): LayoutWorkerConnection => {
  const state: { target: LayoutTarget | null } = { target: null };
  const firstTarget = new Promise<LayoutTarget>(resolve => {
    const switchTarget = (target: LayoutTarget) => {
      state.target = target;
      resolve(target);
    };

    const handleDeadWorker = (
      cause: StartFailureCause & StartTiming,
      worker: Worker | null,
      attempt: number
    ) => {
      worker?.terminate();
      const isLastAttempt = attempt >= MAX_LAYOUT_WORKER_ATTEMPTS;
      onStartFailure({
        ...cause,
        attempt,
        next: isLastAttempt ? 'mainThread' : 'newWorker',
      });
      if (isLastAttempt) {
        switchTarget(MAIN_THREAD);
        return;
      }
      startAttempt(attempt + 1);
    };

    const startAttempt = (attempt: number) => {
      const timedOut = { value: false };
      try {
        const worker = createWorker();
        onWorkerCreated(worker);
        watchWorkerStart(worker, {
          onInitialized: timing => {
            if (timedOut.value) {
              onLateStart({ ...timing, attempt });
            }
            switchTarget({ kind: 'worker', worker });
          },
          onError: (event, timing) =>
            handleDeadWorker(
              { reason: 'error', event, ...timing },
              worker,
              attempt
            ),
          onTimeout: timing => {
            timedOut.value = true;
            onStartFailure({
              reason: 'timeout',
              ...timing,
              attempt,
              next: 'mainThreadWhileWaiting',
            });
            if (!state.target) {
              switchTarget(MAIN_THREAD);
            }
          },
          onAbandoned: timing =>
            handleDeadWorker(
              { reason: 'abandoned', ...timing },
              worker,
              attempt
            ),
        });
      } catch (error) {
        handleDeadWorker(
          {
            reason: 'construction',
            error,
            waitedMs: 0,
            wallClockMs: 0,
            wasHidden: isDocumentHidden(),
          },
          null,
          attempt
        );
      }
    };

    startAttempt(1);
  });

  return {
    getTarget: () =>
      state.target ? Promise.resolve(state.target) : firstTarget,
  };
};
